import { useState } from "react";
import { makeEconomy, saveTankEstimate, nowEpochSeconds, type CanonicalStore, type Car } from "../../core/index.ts";
import { BackRow, parseNum } from "./Modal.tsx";
import { fmt } from "../format.ts";

export function TankCalcForm({ store, car, onSave, onClose }: {
  store: CanonicalStore; car: Car; onSave: (s: CanonicalStore) => void; onClose: () => void;
}) {
  const economy = makeEconomy(store.profile.unitSystem === "metric");
  const dl = economy.distanceLabel();
  const ft = (car.fuelType ?? "").toLowerCase();
  const isEV = ft === "electric";
  const hasFuelCap = (car.tankVolume ?? 0) > 0;
  const hasChargeCap = (car.batteryCapacity ?? 0) > 0;

  const [mode, setMode] = useState<"fuel" | "battery">(isEV ? "battery" : "fuel");
  const battery = mode === "battery";
  const capacity = battery ? car.batteryCapacity ?? 0 : car.tankVolume ?? 0;
  const capUnit = battery ? "kWh" : economy.volumeLabel();
  const last = battery ? car.chargeEstimate : car.tankEstimate;

  // fullRange / rangeLeft are stored in km (backup schema) but entered in the
  // user's display unit, so they convert on the way in and on the way out.
  const showRange = (km: number | null | undefined) =>
    km != null ? String(Math.round(economy.convertDistance(km))) : "";

  const [full, setFull] = useState(showRange(last?.fullRange));
  const [left, setLeft] = useState(showRange(last?.rangeLeft));

  const f = parseNum(full), l = parseNum(left);
  const percent = f > 0 ? Math.min(100, Math.max(0, (l / f) * 100)) : 0;
  const volumeNative = (percent / 100) * capacity;
  const color = battery ? "var(--cyan)" : "var(--amber)";

  const inp: React.CSSProperties = {
    width: "100%", background: "var(--panel2)", border: "1px solid var(--edge)", borderRadius: 10,
    color: "var(--cream)", fontFamily: "var(--mono)", fontSize: 13, padding: "10px 14px",
    boxSizing: "border-box",
  };

  const gaugeId = "gauge-" + mode;

  return (
    <div className="screen">
      <div className="container glow">
        <BackRow onClick={onClose} />
        <div className="screen-title">{battery ? "BATTERY" : "TANK"} CALCULATOR</div>
        <div className="eyebrow amber">{car.model}</div>

        {hasChargeCap && hasFuelCap && (
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button className="btn ghost" style={{ flex: 1, textAlign: "center", fontSize: 10, letterSpacing: 1.6, ...(mode === "fuel" ? { borderColor: "var(--amber)", color: "var(--amber)" } : { opacity: 0.5 }) }} onClick={() => { setMode("fuel"); setFull(showRange(car.tankEstimate?.fullRange)); setLeft(showRange(car.tankEstimate?.rangeLeft)); }}>FUEL</button>
            <button className="btn ghost" style={{ flex: 1, textAlign: "center", fontSize: 10, letterSpacing: 1.6, ...(mode === "battery" ? { borderColor: "var(--cyan)", color: "var(--cyan)" } : { opacity: 0.5 }) }} onClick={() => { setMode("battery"); setFull(showRange(car.chargeEstimate?.fullRange)); setLeft(showRange(car.chargeEstimate?.rangeLeft)); }}>BATTERY</button>
          </div>
        )}

        {capacity <= 0 && (
          <div className="panel mono" style={{ color: "var(--redline)", fontSize: 11, marginTop: 10 }}>
            Set the car's {battery ? "battery capacity (kWh)" : `tank volume (${economy.volumeLabel()})`} first (Edit car).
          </div>
        )}

        <div className="panel" style={{ marginTop: 12, textAlign: "center" }}>
          <svg viewBox="0 0 120 80" style={{ width: 200, height: 133, maxWidth: "100%" }}>
            <defs>
              <linearGradient id={`${gaugeId}-grad`} x1="0" y1="1" x2="0" y2="0">
                <stop offset="0" stopColor={color} stopOpacity="0.15" />
                <stop offset="1" stopColor={color} stopOpacity="1" />
              </linearGradient>
            </defs>
            <path
              d="M 10 70 A 50 50 0 1 1 110 70"
              fill="none" stroke="var(--edge)" strokeWidth="12" strokeLinecap="round"
            />
            <path
              d="M 10 70 A 50 50 0 0 1 110 70"
              fill="none" stroke={`url(#${gaugeId}-grad)`} strokeWidth="12" strokeLinecap="round"
              strokeDasharray={`${percent * 1.57} 157`}
              style={{ transition: "stroke-dasharray 0.6s ease" }}
            />
            <text x="60" y="48" textAnchor="middle" fill={color} fontFamily="var(--mono)" fontSize="22" fontWeight="700">{percent.toFixed(0)}%</text>
            <text x="60" y="66" textAnchor="middle" fill="var(--muted)" fontFamily="var(--mono)" fontSize="8">
              {battery ? volumeNative.toFixed(1) : fmt(economy.convertVolume(volumeNative))} {capUnit}
            </text>
          </svg>
        </div>

        <div className="eyebrow amber" style={{ marginTop: 10 }}>ENTER FROM DASHBOARD</div>
        <div className="mono muted" style={{ fontSize: 9, marginBottom: 8 }}>Full range and current range from your car's display.</div>

        <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>FULL RANGE</label>
        <input style={inp} type="number" placeholder={dl} value={full} onChange={(e) => setFull(e.target.value)} />

        <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6, marginTop: 8 }}>CURRENT RANGE</label>
        <input style={inp} type="number" placeholder={dl} value={left} onChange={(e) => setLeft(e.target.value)} />

        <button className="btn primary" style={{ marginTop: 12 }} disabled={capacity <= 0 || f <= 0 || l < 0} onClick={() => {
          onSave(saveTankEstimate(store, car.id, battery, {
            fullRange: economy.toKm(f), rangeLeft: economy.toKm(l),
            // percent is a ratio of two display-unit values, so it needs no conversion,
            // and volumeNative is already litres/kWh.
            percent, volume: volumeNative, savedAt: nowEpochSeconds(),
          }));
          onClose();
        }}>CALCULATE & SAVE</button>
      </div>
    </div>
  );
}
